import type { ICalendarCell, ICalendarEvent, IEvent } from '@/features/calendar/interfaces'

import type { TCalendarView } from '@/features/calendar/types'
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInDays,
  differenceInMinutes,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isSameWeek,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'

// ================ Header helper functions ================ //

export function rangeText(view: TCalendarView, date: Date) {
  const formatString = 'MMM d, yyyy'
  let start: Date
  let end: Date

  switch (view) {
    case 'month':
      start = startOfMonth(date)
      end = endOfMonth(date)
      break
    case 'week':
      start = startOfWeek(date)
      end = endOfWeek(date)
      break
    case 'day':
      return format(date, formatString)
    default:
      return 'Error while formatting '
  }

  return `${format(start, formatString)} - ${format(end, formatString)}`
}

export function navigateDate(date: Date, view: TCalendarView, direction: 'previous' | 'next'): Date {
  const operations = {
    month: direction === 'next' ? addMonths : subMonths,
    week: direction === 'next' ? addWeeks : subWeeks,
    day: direction === 'next' ? addDays : subDays,
  }

  return operations[view](date, 1)
}

export function getEventsCount(events: IEvent[], date: Date, view: TCalendarView): number {
  const compareFns = {
    day: isSameDay,
    week: isSameWeek,
    month: isSameMonth,
  }

  return events.filter(event => compareFns[view](new Date(event.startDate), date)).length
}

export function getTrainingsCount(trainings: ICalendarEvent[], date: Date, view: TCalendarView): number {
  const compareFns = {
    day: isSameDay,
    week: isSameWeek,
    month: isSameMonth,
  }

  return trainings.filter(training => compareFns[view](new Date(training.startAt), date)).length
}

// ================ Week and day view helper functions ================ //

export function groupEvents(dayEvents: IEvent[]) {
  const sortedEvents = dayEvents.sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime())
  const groups: IEvent[][] = []

  for (const event of sortedEvents) {
    const eventStart = parseISO(event.startDate)

    let placed = false
    for (const group of groups) {
      const lastEventInGroup = group[group.length - 1]
      const lastEventEnd = parseISO(lastEventInGroup.endDate)

      if (eventStart >= lastEventEnd) {
        group.push(event)
        placed = true
        break
      }
    }

    if (!placed)
      groups.push([event])
  }

  return groups
}

export function groupTrainings(dayTrainings: ICalendarEvent[]) {
  const sortedTrainings = dayTrainings.sort((a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime())
  const groups: ICalendarEvent[][] = []

  for (const training of sortedTrainings) {
    const trainingStart = parseISO(training.startAt)

    let placed = false
    for (const group of groups) {
      const lastEventInGroup = group[group.length - 1]
      const lastEventEnd = parseISO(lastEventInGroup.endAt)

      if (trainingStart >= lastEventEnd) {
        group.push(training)
        placed = true
        break
      }
    }

    if (!placed)
      groups.push([training])
  }

  return groups
}

export function getEventBlockStyle(event: IEvent, day: Date, groupIndex: number, groupSize: number) {
  const startDate = parseISO(event.startDate)
  const dayStart = new Date(day.setHours(0, 0, 0, 0))
  const eventStart = startDate < dayStart ? dayStart : startDate
  const startMinutes = differenceInMinutes(eventStart, dayStart)

  const top = (startMinutes / 1440) * 100
  const width = 100 / groupSize
  const left = groupIndex * width

  return { top: `${top}%`, width: `${width}%`, left: `${left}%` }
}

export function getTrainingBlockStyle(training: ICalendarEvent, day: Date, groupIndex: number, groupSize: number) {
  const startDate = parseISO(training.startAt)
  const dayStart = new Date(day.setHours(0, 0, 0, 0))
  const trainingStart = startDate < dayStart ? dayStart : startDate
  const startMinutes = differenceInMinutes(trainingStart, dayStart)

  const top = (startMinutes / 1440) * 100
  const width = 100 / groupSize
  const left = groupIndex * width

  return { top: `${top}%`, width: `${width}%`, left: `${left}%` }
}

// ================ Month view helper functions ================ //

export function getCalendarCells(selectedDate: Date): ICalendarCell[] {
  const currentYear = selectedDate.getFullYear()
  const currentMonth = selectedDate.getMonth()

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay()

  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth)
  const daysInPrevMonth = getDaysInMonth(currentYear, currentMonth - 1)
  const totalDays = firstDayOfMonth + daysInMonth

  const prevMonthCells = Array.from({ length: firstDayOfMonth }, (_, i) => ({
    day: daysInPrevMonth - firstDayOfMonth + i + 1,
    currentMonth: false,
    date: new Date(currentYear, currentMonth - 1, daysInPrevMonth - firstDayOfMonth + i + 1),
  }))

  const currentMonthCells = Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    currentMonth: true,
    date: new Date(currentYear, currentMonth, i + 1),
  }))

  const nextMonthCells = Array.from({ length: (7 - (totalDays % 7)) % 7 }, (_, i) => ({
    day: i + 1,
    currentMonth: false,
    date: new Date(currentYear, currentMonth + 1, i + 1),
  }))

  return [...prevMonthCells, ...currentMonthCells, ...nextMonthCells]
}

export function calculateMonthEventPositions(multiDayEvents: IEvent[], singleDayEvents: IEvent[], selectedDate: Date) {
  const monthStart = startOfMonth(selectedDate)
  const monthEnd = endOfMonth(selectedDate)

  const eventPositions: { [key: string]: number } = {}
  const occupiedPositions: { [key: string]: boolean[] } = {}

  eachDayOfInterval({ start: monthStart, end: monthEnd }).forEach((day) => {
    occupiedPositions[day.toISOString()] = [false, false, false]
  })

  const sortedEvents = [
    ...multiDayEvents.sort((a, b) => {
      const aDuration = differenceInDays(parseISO(a.endDate), parseISO(a.startDate))
      const bDuration = differenceInDays(parseISO(b.endDate), parseISO(b.startDate))
      return bDuration - aDuration || parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()
    }),
    ...singleDayEvents.sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()),
  ]

  sortedEvents.forEach((event) => {
    const eventStart = parseISO(event.startDate)
    const eventEnd = parseISO(event.endDate)
    const eventDays = eachDayOfInterval({
      start: eventStart < monthStart ? monthStart : eventStart,
      end: eventEnd > monthEnd ? monthEnd : eventEnd,
    })

    let position = -1

    for (let i = 0; i < 3; i++) {
      if (
        eventDays.every((day) => {
          const dayPositions = occupiedPositions[startOfDay(day).toISOString()]
          return dayPositions && !dayPositions[i]
        })
      ) {
        position = i
        break
      }
    }

    if (position !== -1) {
      eventDays.forEach((day) => {
        const dayKey = startOfDay(day).toISOString()
        occupiedPositions[dayKey][position] = true
      })
      eventPositions[event.id] = position
    }
  })

  return eventPositions
}

export function calculateMonthTrainingPositions(multiDayTrainings: ICalendarEvent[], singleDayTrainings: ICalendarEvent[], selectedDate: Date) {
  const monthStart = startOfMonth(selectedDate)
  const monthEnd = endOfMonth(selectedDate)

  const trainingPositions: { [key: string]: number } = {}
  const occupiedPositions: { [key: string]: boolean[] } = {}

  eachDayOfInterval({ start: monthStart, end: monthEnd }).forEach((day) => {
    occupiedPositions[day.toISOString()] = [false, false, false]
  })

  const sortedTrainings = [
    ...multiDayTrainings.sort((a, b) => {
      const aDuration = differenceInDays(parseISO(a.endAt), parseISO(a.startAt))
      const bDuration = differenceInDays(parseISO(b.endAt), parseISO(b.startAt))
      return bDuration - aDuration || parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime()
    }),
    ...singleDayTrainings.sort((a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime()),
  ]

  sortedTrainings.forEach((training) => {
    const trainingStart = parseISO(training.startAt)
    const trainingEnd = parseISO(training.endAt)
    const trainingDays = eachDayOfInterval({
      start: trainingStart < monthStart ? monthStart : trainingStart,
      end: trainingEnd > monthEnd ? monthEnd : trainingEnd,
    })

    let position = -1

    for (let i = 0; i < 3; i++) {
      if (
        trainingDays.every((day) => {
          const dayPositions = occupiedPositions[startOfDay(day).toISOString()]
          return dayPositions && !dayPositions[i]
        })
      ) {
        position = i
        break
      }
    }

    if (position !== -1) {
      trainingDays.forEach((day) => {
        const dayKey = startOfDay(day).toISOString()
        occupiedPositions[dayKey][position] = true
      })
      trainingPositions[training.id] = position
    }
  })

  return trainingPositions
}

export function getMonthCellEvents(date: Date, events: IEvent[], eventPositions: Record<string, number>) {
  const eventsForDate = events.filter((event) => {
    const eventStart = parseISO(event.startDate)
    const eventEnd = parseISO(event.endDate)
    return (date >= eventStart && date <= eventEnd) || isSameDay(date, eventStart) || isSameDay(date, eventEnd)
  })

  return eventsForDate
    .map(event => ({
      ...event,
      position: eventPositions[event.id] ?? -1,
      isMultiDay: !isSameDay(parseISO(event.startDate), parseISO(event.endDate)),
    }))
    .sort((a, b) => {
      if (a.isMultiDay && !b.isMultiDay)
        return -1
      if (!a.isMultiDay && b.isMultiDay)
        return 1
      return a.position - b.position
    })
}

export function getMonthCellTrainings(date: Date, trainings: ICalendarEvent[], trainingPositions: Record<string, number>) {
  const trainingsForDate = trainings.filter((training) => {
    const trainingStart = parseISO(training.startAt)
    const trainingEnd = parseISO(training.endAt)
    return (date >= trainingStart && date <= trainingEnd) || isSameDay(date, trainingStart) || isSameDay(date, trainingEnd)
  })

  return trainingsForDate
    .map(training => ({
      ...training,
      position: trainingPositions[training.id] ?? -1,
      isMultiDay: !isSameDay(parseISO(training.startAt), parseISO(training.endAt)),
    }))
    .sort((a, b) => {
      if (a.isMultiDay && !b.isMultiDay)
        return -1
      if (!a.isMultiDay && b.isMultiDay)
        return 1
      return a.position - b.position
    })
}
